/**
 * Undo Store
 *
 * Manages single-level undo functionality for destructive workspace operations.
 * Tracks the last undoable action with a 10-second expiration window.
 *
 * Features:
 * - Single-level undo (no full history stack)
 * - Auto-expire after 10 seconds
 * - Supports trash and move operations
 * - Keyboard shortcut compatible
 *
 * @module store/useUndoStore
 */

import { create } from 'zustand';

// ============================================================================
// TYPES
// ============================================================================

export interface UndoAction {
  /** Human-readable description of the action */
  description: string;
  /** Function to execute to undo the action */
  undoFn: () => Promise<void>;
  /** Timestamp when action was recorded */
  timestamp: number;
}

interface UndoState {
  /** Current undoable action (null if none available) */
  action: UndoAction | null;
  /** Timer ID for auto-expiration */
  timerId: NodeJS.Timeout | null;
  /** Set an undoable action with auto-expiration */
  setAction: (action: Omit<UndoAction, 'timestamp'>) => void;
  /** Clear the current undoable action */
  clearAction: () => void;
  /** Execute undo if available and not expired */
  executeUndo: () => Promise<boolean>;
  /** Check if undo is available and not expired */
  canUndo: () => boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Undo expiration time in milliseconds (10 seconds) */
const UNDO_EXPIRATION_MS = 10000;

// ============================================================================
// STORE
// ============================================================================

/**
 * Undo store implementation
 *
 * Provides single-level undo functionality with automatic expiration.
 * When an action is set, it automatically expires after 10 seconds.
 */
export const useUndoStore = create<UndoState>((set, get) => ({
  action: null,
  timerId: null,

  /**
   * Set an undoable action with auto-expiration
   *
   * @param action - Action details (description and undo function)
   */
  setAction: (action) => {
    const state = get();

    // Clear existing timer if present
    if (state.timerId) {
      clearTimeout(state.timerId);
    }

    // Set new action with timestamp
    const newAction: UndoAction = {
      ...action,
      timestamp: Date.now(),
    };

    // Set up auto-expiration timer
    const timerId = setTimeout(() => {
      set({ action: null, timerId: null });
    }, UNDO_EXPIRATION_MS);

    set({ action: newAction, timerId });
  },

  /**
   * Clear the current undoable action
   */
  clearAction: () => {
    const state = get();

    // Clear timer if present
    if (state.timerId) {
      clearTimeout(state.timerId);
    }

    set({ action: null, timerId: null });
  },

  /**
   * Execute undo if available and not expired
   *
   * @returns True if undo was executed, false if no action available or expired
   */
  executeUndo: async () => {
    const state = get();

    if (!state.canUndo()) {
      return false;
    }

    try {
      // Execute the undo function
      await state.action!.undoFn();

      // Clear the action after successful undo
      state.clearAction();

      return true;
    } catch (error) {
      // Clear action even on error to prevent retry loops
      state.clearAction();
      throw error;
    }
  },

  /**
   * Check if undo is available and not expired
   *
   * @returns True if undo is available and within expiration window
   */
  canUndo: () => {
    const state = get();

    if (!state.action) {
      return false;
    }

    const elapsed = Date.now() - state.action.timestamp;
    return elapsed < UNDO_EXPIRATION_MS;
  },
}));
